import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LanguageProvider } from './contexts/LanguageContext';
import LandingPage from './components/LandingPage';
import { trackFirstPartyPageView, trackPageView } from './utils/analytics';
import { APOLLO_APP_ID, APOLLO_TRACKER_URL } from './config/appConfig';
import './App.css';

const Dashboard = lazy(() => import('./components/Dashboard'));
const Login = lazy(() => import('./components/Login'));
const SignUp = lazy(() => import('./components/SignUp'));
const ForgetPassword = lazy(() => import('./components/ForgetPassword'));
const FeaturesPage = lazy(() => import('./components/FeaturesPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const ResourcesPage = lazy(() => import('./components/ResourcesPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ContactSalesPage = lazy(() => import('./components/ContactSalesPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const ContractChatPage = lazy(() => import('./components/ContractChatPage'));
const MainSummariesPage = lazy(() => import('./components/MainSummariesPage'));
const DeviationRiskPage = lazy(() => import('./components/DeviationRiskPage'));
const AmendedContractPage = lazy(() => import('./components/AmendedContractPage'));
const ViewContractPage = lazy(() => import('./components/ViewContractPage'));
const Payment = lazy(() => import('./components/Payment'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const PolicyPage = lazy(() => import('./components/PolicyPage'));
const UnsubscribePage = lazy(() => import('./components/UnsubscribePage'));
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const BlogListPage = lazy(() => import('./components/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./components/blog/BlogPostPage'));
const MarketingLogin = lazy(() => import('./marketing/MarketingLogin'));
const MarketingLayout = lazy(() => import('./marketing/MarketingLayout'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

const LOCAL_APOLLO_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

export const shouldLoadApolloTracker = (hostname = '') => {
  const normalizedHostname = hostname.replace(/^\[|\]$/g, '').toLowerCase();
  const isPrivateIpv4 =
    /^10\./.test(normalizedHostname) ||
    /^192\.168\./.test(normalizedHostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(normalizedHostname) ||
    /^169\.254\./.test(normalizedHostname);
  const isPrivateIpv6 = normalizedHostname.startsWith('fc') || normalizedHostname.startsWith('fd') || normalizedHostname.startsWith('fe80:');

  return Boolean(normalizedHostname) && !LOCAL_APOLLO_HOSTNAMES.has(normalizedHostname) && !isPrivateIpv4 && !isPrivateIpv6;
};

const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="mx-auto mb-4 h-10 w-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      <p className="text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // The unsubscribe link carries a personal token in its query: never send it to GA.
    if (location.pathname === '/unsubscribe') return;
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // First-party analytics counts page views by pathname only.
  useEffect(() => {
    trackFirstPartyPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

const ApolloTracker = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (!APOLLO_APP_ID || !APOLLO_TRACKER_URL || !shouldLoadApolloTracker(window.location.hostname)) {
      return;
    }

    if (window.__jelaniApolloTrackerInitialized || document.querySelector('[data-apollo-tracker="true"]')) {
      return;
    }

    window.__jelaniApolloTrackerInitialized = true;

    const cacheBust = Math.random().toString(36).substring(7);
    const script = document.createElement('script');
    script.src = `${APOLLO_TRACKER_URL}?nocache=${cacheBust}`;
    script.async = true;
    script.defer = true;
    script.setAttribute('async', '');
    script.setAttribute('defer', '');
    script.dataset.apolloTracker = 'true';
    script.onload = () => {
      if (typeof window.trackingFunctions?.onLoad === 'function') {
        window.trackingFunctions.onLoad({
          appId: APOLLO_APP_ID
        });
      }
    };

    document.head.appendChild(script);
  }, []);

  return null;
};

function App() {
  return (
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ApolloTracker />
          <AnalyticsTracker />
          <div className="App">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* New public marketing page? Add it to PUBLIC_PATHS in utils/analytics.js and apps/analytics/paths.py */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forget-password" element={<ForgetPassword />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment" element={<Payment />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactSalesPage />} />
                <Route path="/contact-sales" element={<ContactSalesPage />} />
                <Route path="/terms" element={<PolicyPage type="terms" />} />
                <Route path="/code-of-conduct" element={<PolicyPage type="conduct" />} />
                <Route path="/ethics" element={<PolicyPage type="ethics" />} />
                <Route path="/privacy-policy" element={<PolicyPage type="privacy" />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/contract-chat/:contractId" element={<ContractChatPage />} />
                <Route path="/main-summaries/:contractId" element={<MainSummariesPage />} />
                <Route path="/deviation-risk/:contractId" element={<DeviationRiskPage />} />
                <Route path="/amended-contract/:contractId" element={<AmendedContractPage />} />
                <Route path="/view-contract/:contractId" element={<ViewContractPage />} />
                <Route path="/adminLogin" element={<AdminLogin />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/blog" element={<BlogListPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/marketing/login" element={<MarketingLogin />} />
                <Route path="/marketing/*" element={<MarketingLayout />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </QueryClientProvider>
    </LanguageProvider>
  );
}

export default App;
